import { BodyText } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { Coverage } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { EditableRecordButton } from "../../components/EditableRecordButton";
import {
  type HealthInsuranceCoverage,
  useBillingCancelInsurance,
  useBillingHealthInsurance,
} from "../network/useHealthInsurance";
import { HealthInsuranceForm } from "./HealthInsuranceForm";

export const HealthInsurancePlan = ({
  coverage,
}: { coverage: HealthInsuranceCoverage }) => {
  const { patientId } = useParams({ strict: false });
  const [isEditing, setIsEditing] = useState(false);
  const { mutate: saveBillingCoverage, isPending: isPendingBilling } =
    useBillingHealthInsurance(patientId as string, () => setIsEditing(false));
  const { mutate: cancelBilling, isPending: isCancellingBilling } =
    useBillingCancelInsurance({
      patientId: patientId as string,
      onSuccess: () => setIsEditing(false),
    });

  if (!patientId) {
    return null;
  }

  const x = Object.entries(Coverage.SubscriberRelationship.values).find(
    ([, value]) => value === coverage?.subscriberRelationship,
  )?.[0];

  const subscriberRelationshipDisplay = x
    ? Coverage.SubscriberRelationship.display[
        x as keyof typeof Coverage.SubscriberRelationship.values
      ]
    : "";

  return (
    <div>
      {isEditing ? (
        <div className="flex relative flex-col gap-2 px-3 py-2 w-full">
          <HealthInsuranceForm
            existingCoverage={coverage}
            onSubmit={(coverageDetails) => {
              saveBillingCoverage({
                carrierId: coverageDetails.payorOrganizationId,
                memberId: coverageDetails.memberId,
                patientRelationship: coverageDetails.subscriberRelationship as
                  | "self"
                  | "spouse"
                  | "child"
                  | "other",
                planName: coverageDetails.planCode,
                groupNumber: coverageDetails.groupNumber,
                customerServicePhone:
                  coverageDetails.customerServicePhoneNumber,
              });
            }}
            onCancel={() => setIsEditing(false)}
            isSaving={isPendingBilling}
          />
        </div>
      ) : (
        <EditableRecordButton
          onClick={() => {
            trackEvent(TRACKERS.EDIT_INSURANCE);
            setIsEditing(true);
          }}
          containerClassName="gap-3"
          onDelete={() => {
            cancelBilling();
          }}
          deletePending={isCancellingBilling}
        >
          <div className="flex flex-row gap-2">
            <div className="flex flex-col flex-1 gap-2">
              <BodyText bold size="sm">
                Insurance Carrier
              </BodyText>
              <BodyText size="sm">{coverage.payorOrganizationName}</BodyText>
            </div>
            <div className="flex flex-col flex-1 gap-2">
              <BodyText bold size="sm">
                Member ID
              </BodyText>
              <BodyText size="sm">{coverage.memberIdentifier}</BodyText>
            </div>
          </div>
          <div className="flex flex-row gap-2 pr-5">
            <div className="flex flex-col flex-1 gap-2">
              <BodyText bold size="sm">
                Plan
              </BodyText>
              <BodyText size="sm">{coverage.planCode}</BodyText>
            </div>
            <div className="flex flex-col flex-1 gap-2">
              <BodyText bold size="sm">
                Group Number
              </BodyText>
              <BodyText size="sm">{coverage.groupNumber}</BodyText>
            </div>
          </div>
          <div className="flex flex-row gap-2 pr-5">
            <div className="flex flex-col flex-1 gap-2">
              <BodyText bold size="sm">
                Customer Service Number
              </BodyText>
              <BodyText size="sm">
                {coverage.customerServicePhoneNumber ?? "Unknown"}
              </BodyText>
            </div>
            <div className="flex flex-col flex-1 gap-2">
              <BodyText bold size="sm">
                Policy Holder
              </BodyText>
              <BodyText size="sm">{subscriberRelationshipDisplay}</BodyText>
            </div>
          </div>
        </EditableRecordButton>
      )}
    </div>
  );
};
